import React, { Component } from 'react'
import { observer } from 'mobx-react'
import i18n from 'i18next'
import { v4 as uuidv4 } from 'uuid'
import * as R from 'ramda'
import Modal from '../../../../UniformComponents/Modal'
import Button from '../../../../UniformComponents/Button'
import ModalFooter from '../../../../UniformComponents/ModalFooter'
import Accordion from '../../../Report/components/Accordion'
import Select from '../../../form/Select'
import ProductEditStore from '../../../../Product/ProductEditStore'
import { numVisitors, numExhibitors } from '../../../../utils/Globals/SurveyFieldOptions'

@observer
class BenchmarksModal extends Component {
    ruleTitles = {
        3: 'Client',
        4: 'Division',
        5: 'Portfolio',
        6: 'Product',
        7: 'Survey',
    }

    ruleObjects = [
        {
            title: 'Product',
            oId: 6,
            fields: [
                {
                    description: i18n.t('views.product.wizard.EV.form.schedulevalue.label'),
                    value: 'frequency',
                },
                {
                    description: i18n.t('views.product.wizard.EV.form.yearrun.label'),
                    value: 'yearRun',
                    hide: (customTag) => customTag.industry === 'CE',
                },
                {
                    description: i18n.t('views.survey.wizard.EV.stage.1.event.sectors.level1'),
                    value: 'sector',
                },
                {
                    description: i18n.t('views.survey.wizard.CE.stage.1.event.sectors.level1'),
                    value: 'typeOfEvent',
                    hide: (customTag) => customTag.industry === 'EV',
                },
            ],
        },
        {
            title: 'Survey',
            oId: 7,
            fields: [
                {
                    description: i18n.t('views.survey.wizard.CE.stage.2.details.visitors.label'),
                    value: 'numVisitors',
                    hide: (customTag) => customTag.industry === 'CE',
                },
                {
                    description: i18n.t('views.survey.wizard.CE.stage.2.details.exhibitors.label'),
                    value: 'numExhibitors',
                    hide: (customTag) => customTag.industry === 'CE',
                },
                {
                    description: i18n.t('country'),
                    value: 'country',
                },
                {
                    description: i18n.t('views.product.wizard.VA.form.visattrncity.label'),
                    value: 'city',
                },
                {
                    description: i18n.t('venues'),
                    value: 'venue',
                    hide: (customTag) => customTag.industry === 'CE',
                },
                {
                    description: i18n.t('views.survey.wizard.CE.stage.2.details.Languages.label'),
                    value: 'locales',
                    hide: (customTag) => customTag.industry === 'EV',
                },
                {
                    description: i18n.t('survey_default_language'),
                    value: 'defaultLocales',
                    hide: (customTag) => customTag.industry === 'EV',
                },
            ],
        },
    ]

    constructor(props) {
        super(props)
        const productStore = new ProductEditStore()

        this.state = {
            options: {
                frequency: productStore.productScheduleCE,
                numVisitors,
                numExhibitors,
                yearRun: productStore.productYearRun,
            },
        }
        this.getCustomTagOptions()
        this.getOptions()
    }

    filteredTagList = () => {
        const { store } = this.props
        const { tagList } = store
        return tagList.filter((tag) => (
            ['SB', 'CH'].includes(tag.questionType)
            && [3, 4, 5, 6, 7].includes(tag.oid)))
    }

    getCustomTagOptions = async () => {
        const { store } = this.props
        await store.initSearch()
        const { langcode } = store.store
        this.setState((prevState) => ({
            options: {
                ...prevState.options,
                ...this.filteredTagList().reduce((options, option) => (
                    {
                        ...options,
                        [option.id]: R.pathOr([], ['config', 'answerOptions'], option).map((ao) => (
                            { label: R.pathOr('', ['config', 'langs', langcode], ao), value: ao.uuid }
                        )),
                    }
                ), {}),
            },
        }))
    }

    getOptions = async () => {
        const { store } = this.props
        const { store: appStore } = store
        const { customTag } = store

        const [countries, cities, venues, sectors, eventTypes, audiences] = await Promise.all([
            appStore.http.get('/refcode/country'),
            appStore.http.get('/cities'),
            appStore.http.get('/venues'),
            appStore.http.get(`/taxonomy/vocabulary/${customTag.industry === 'CE' ? 5 : 1}`),
            appStore.http.get('/taxonomy/vocabulary/3'),
            appStore.http.get('/taxonomy/parent/302'),
        ])

        this.setState((prevState) => ({
            options: {
                ...prevState.options,
                country: countries.response.map((c) => ({ value: c.name, label: c.description })),
                city: cities.map((c) => ({ value: c.city, label: c.city })),
                venue: venues.map((v) => ({ value: v.venueId, label: v.name })),
                locales: appStore.localeOptions,
                defaultLocales: appStore.localeOptions,
                sector: sectors.map((s) => ({ value: s.termId, label: s.label })),
                typeOfEvent: eventTypes.map((et) => ({ value: et.termId, label: et.label })),
                audience: audiences.map((a) => ({ value: a.termId, label: a.label })),
            },
        }))
    }

    updateBenchmarkOptions = (benchmarkOptions) => {
        const { store } = this.props
        const { onBlur } = store

        onBlur(
            { value: benchmarkOptions, type: 'benchmarkOptions' },
            null,
            null,
            ['benchmarks', 'benchmarkOptions'],
        )
    }

    addRule = (rule, oId) => {
        const { store } = this.props
        const { customTag, benchamrkOptionIndex } = store
        const { config } = customTag

        const benchmarkOptions = config.benchmarkRules.slice()
        const currentOption = benchmarkOptions[benchamrkOptionIndex]

        currentOption.config.rules.push({
            uuid: uuidv4(),
            oId,
            rule: rule.value,
            description: rule.description,
            operator: currentOption.config.rules.length > 0 ? 'AND' : undefined,
            valueOperator: 'OR',
            values: [],
        })

        this.updateBenchmarkOptions(benchmarkOptions)
    }

    removeRule = (ruleId) => {
        const { store } = this.props
        const { customTag, benchamrkOptionIndex } = store
        const { config } = customTag

        const benchmarkOptions = config.benchmarkRules.slice()
        const currentOption = benchmarkOptions[benchamrkOptionIndex]

        currentOption.config.rules = currentOption.config.rules
            .filter((rule) => rule.uuid !== ruleId)

        this.updateBenchmarkOptions(benchmarkOptions)
    }

    updateRule = ({ name, value }, index) => {
        const { store } = this.props
        const { customTag, benchamrkOptionIndex } = store
        const { config } = customTag
        const benchmarkOptions = config.benchmarkRules.slice()
        const currentOption = benchmarkOptions[benchamrkOptionIndex]
        currentOption.config.rules = currentOption.config.rules.slice()
        currentOption.config.rules[index][name] = R.type(value) === 'Array'
            ? value.map((v) => v.value)
            : value

        this.updateBenchmarkOptions(benchmarkOptions)
    }

    renderRules = () => {
        const { store } = this.props
        const { customTag, benchamrkOptionIndex } = store
        const { config } = customTag

        // if (!benchmarks) return null

        const benchmarkOptions = config.benchmarkRules.slice()
        const currentOption = benchmarkOptions[benchamrkOptionIndex]
        const { options } = this.state
        return (
            <div className="rules">
                {R.pathOr([], ['config', 'rules'], currentOption).map((rule, i) => (
                    <div className="rule" key={rule.uuid}>
                        <div className="ruleSettings">
                            {/* {rule.operator
                            ? (
                                <div className="operator">
                                    <Select
                                        name="operator"
                                        defaultValue={rule.operator}
                                        options={[{ value: 'AND', label: 'AND' }, { value: 'OR', label: 'OR' }]}
                                        onBlur={(obj) => this.updateRule(obj, i)}
                                    />
                                </div>
                            )
                            : null} */}
                            {i > 0
                                ? (
                                    <div className="operator">
                                        AND
                                    </div>
                                )
                                : null }
                            <div className="ruleName">
                                {this.ruleTitles[rule.oId]}
                                {' '}
                                -
                                {' '}
                                {rule.description}
                                {' '}
                                <span>
                                    is one of:
                                </span>
                            </div>
                            <div className="ruleOptions">
                                <Select
                                    name="values"
                                    defaultValue={R.or(rule.values, []).slice()}
                                    options={R.pathOr([], [rule.rule], options)}
                                    isMulti
                                    onBlur={(obj) => this.updateRule(obj, i)}
                                />
                            </div>
                        </div>
                        <div className="removeRule">
                            <i className="fa-regular fa-trash" onClick={() => this.removeRule(rule.uuid)} />
                        </div>
                    </div>
                ))}
            </div>
        )
    }

    getCustomTagRules = () => {
        const { store } = this.props
        const { customTag } = store

        const tagRules = this.filteredTagList()
            .reduce((rules, tag) => {
                if ((tag.clientId === customTag.clientId)
                    || (customTag.clientId !== 0 && tag.clientId === 0)) {
                    rules[tag.oid].fields.push({
                        description: tag.name,
                        value: tag.id,
                    })
                }
                return rules
            }, {
                3: {
                    title: i18n.t('customTag.benchmarks.customTagBenchmarks.client'),
                    oId: 3,
                    fields: [],
                },
                4: {
                    title: i18n.t('customTag.benchmarks.customTagBenchmarks.division'),
                    oId: 4,
                    fields: [],
                },
                5: {
                    title: i18n.t('customTag.benchmarks.customTagBenchmarks.portfolio'),
                    oId: 5,
                    fields: [],
                },
                6: {
                    title: i18n.t('customTag.benchmarks.customTagBenchmarks.event'),
                    oId: 6,
                    fields: [],
                },
                7: {
                    title: i18n.t('customTag.benchmarks.customTagBenchmarks.survey'),
                    oId: 7,
                    fields: [],
                },
            })
        return Object.values(tagRules).filter((tr) => tr.fields.length)
    }

    renderRuleObjects = () => {
        const { store } = this.props
        const { customTag } = store
        const customTagRules = this.getCustomTagRules()
        return (
            <div className="ruleObjects">
                {[...this.ruleObjects, ...customTagRules].map((ro) => (
                    <div className="ruleObject">
                        <Accordion
                            cssClass="ruleAccordion"
                            title={(
                                <div className="title">
                                    {ro.title}
                                </div>
                            )}
                        >
                            {ro.fields.filter((f) => (f.hide ? !f.hide(customTag) : true)).map((f) => (
                                <div style={{
                                    display: 'flex', gap: '5px', alignItems: 'center', margin: '5px 0',
                                }}
                                >
                                    {f.description}
                                    <i
                                        className="fa-regular fa-circle-plus"
                                        onClick={() => this.addRule(f, ro.oId)}
                                    />
                                </div>
                            ))}
                        </Accordion>
                    </div>
                ))}
            </div>
        )
    }

    render() {
        const { store } = this.props
        const { showBenchmarksModal, toggleShowBenchmarksModal } = store
        return (
            <Modal
                show={showBenchmarksModal}
                onClose={toggleShowBenchmarksModal}
                cssClass="benchmarksModal"
            >
                <div className="body">
                    {this.renderRuleObjects()}
                    {this.renderRules()}
                </div>
                <ModalFooter>
                    <div className="footerControls">
                        <Button
                            label="Close"
                            cssClass="tertiary regular"
                            style={{
                                width: '134px', fontWeight: 600, textAlign: 'center',
                            }}
                            onClick={toggleShowBenchmarksModal}
                        />
                    </div>
                </ModalFooter>
            </Modal>
        )
    }
}

export default BenchmarksModal
